import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import TimeSelect from "@/components/TimeSelect";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Save, CalendarPlus, Trash2 } from "lucide-react";
import { todayISO, formatDMY } from "@/lib/dateFormat";

/**
 * Reusable dialog to create an extra/reschedule/makeup session for a group.
 * Props:
 *   open, onOpenChange
 *   groupId (required)
 *   defaultDate (optional, ISO YYYY-MM-DD)
 *   enrollments (optional, used to render the "specific student" selector)
 *   usersMap (optional, dict user_id → user, for showing names)
 *   onCreated (callback)
 */
export default function ExtraSessionDialog({
  open, onOpenChange, groupId, defaultDate, enrollments = [], usersMap = {}, onCreated,
  // When `preset` is provided the dialog opens with those defaults and hides
  // the type selector (used by the "Reprogramar sesión" chip action).
  // Shape: { type: 'reschedule'|'extra'|'makeup', original_class_date?: string }
  preset = null,
  // Full extra_session doc when editing an existing one (PUT instead of POST).
  existing = null,
  // Called when the user asks to undo/delete the existing session.
  onRequestUndo = null,
}) {
  const { t } = useI18n();
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);   // choque de horario al mover una clase reservada
  const [form, setForm] = useState({
    date: defaultDate || todayISO(),
    type: preset?.type || "extra",
    enrollment_id: "__all__",
    start_time: "",
    duration_minutes: "",
    original_class_date: preset?.original_class_date || "",
    counts_toward_cycle: true,
    generates_charge: false,
    charge_amount: "",
    charge_currency: "USD",
    notes: "",
  });

  useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...f,
        date: defaultDate || todayISO(),
        type: preset?.type || f.type,
        original_class_date: preset?.original_class_date || f.original_class_date,
        ...(existing
          ? {
              enrollment_id: existing.enrollment_id || "__all__",
              start_time: existing.start_time || "",
              duration_minutes: existing.duration_minutes ?? "",
              counts_toward_cycle: existing.counts_toward_cycle !== false,
              generates_charge: !!existing.generates_charge,
              charge_amount: existing.charge_amount ?? "",
              charge_currency: existing.charge_currency || "USD",
              notes: existing.notes || "",
            }
          : {}),
      }));
    }
  }, [open, defaultDate, preset?.type, preset?.original_class_date, existing?.id]);

  const submit = async (force = false) => {
    if (!groupId) {
      toast.error(t("error_generic"));
      return;
    }
    if (form.type === "reschedule" && !form.original_class_date) {
      toast.error(t("reschedule_needs_original"));
      return;
    }
    setSaving(true);
    try {
      const body = {
        group_id: groupId,
        date: form.date,
        type: form.type,
        counts_toward_cycle: !!form.counts_toward_cycle,
        generates_charge: !!form.generates_charge,
        notes: form.notes || "",
      };
      if (form.enrollment_id && form.enrollment_id !== "__all__") {
        body.enrollment_id = form.enrollment_id;
      }
      if (form.start_time) body.start_time = form.start_time;
      if (form.duration_minutes) body.duration_minutes = Number(form.duration_minutes);
      if (form.type === "reschedule") body.original_class_date = form.original_class_date;
      if (form.generates_charge) {
        if (form.charge_amount) body.charge_amount = Number(form.charge_amount);
        body.charge_currency = form.charge_currency || "USD";
      }
      let r;
      if (existing?.id) {
        // Editing: update in place so we never create a duplicate reschedule
        delete body.group_id;
        delete body.enrollment_id;
        if (force) body.force = true;
        r = await api.put(`/extra-sessions/${existing.id}`, body);
        toast.success(r.data?.moved_booking ? t("booked_class_moved") : t("extra_session_updated"));
      } else {
        r = await api.post("/extra-sessions", body);
        toast.success(t("extra_session_created"));
      }
      setConflict(null);
      if (onCreated) onCreated(r.data);
      onOpenChange(false);
    } catch (e) {
      if (e?.response?.status === 409) {
        setConflict(e.response.data?.detail || t("error_generic"));
        return;
      }
      toast.error(e?.response?.data?.detail || t("error_generic"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="extra-session-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarPlus className="h-5 w-5 text-brand" />
            {existing?.booking_id ? t("move_booked_class_title")
              : existing?.id ? t("edit_session_title")
              : preset?.type === "reschedule" ? t("reschedule_session_title")
              : t("new_extra_session")}
          </DialogTitle>
          <DialogDescription>
            {existing?.id
              ? t("edit_session_subtitle", { date: formatDMY(existing.date) || "" })
              : preset?.type === "reschedule"
              ? t("reschedule_session_subtitle", { date: formatDMY(preset.original_class_date) || "" })
              : t("new_extra_session_subtitle")}
          </DialogDescription>
        </DialogHeader>

        {existing?.booking_id && (
          <p className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-lg p-2.5"
             data-testid="booked-class-note">
            {t("move_booked_class_note")}
          </p>
        )}

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {!preset && (
              <div>
                <Label className="text-xs">{t("session_type")}</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="session-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="extra">{t("session_type_extra")}</SelectItem>
                    <SelectItem value="reschedule">{t("session_type_reschedule")}</SelectItem>
                    <SelectItem value="makeup">{t("session_type_makeup")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className={preset ? "col-span-2" : ""}>
              <Label className="text-xs">{preset?.type === "reschedule" ? t("new_date") : t("date")}</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="session-date-input"
              />
              {preset?.type === "reschedule" && preset.original_class_date && (
                <p className="text-[11px] text-slate-500 mt-1">
                  {t("rescheduled_from", { date: formatDMY(preset.original_class_date) })}
                </p>
              )}
            </div>
          </div>

          {form.type === "reschedule" && !preset && (
            <div>
              <Label className="text-xs">{t("original_class_date")}</Label>
              <Input
                type="date"
                value={form.original_class_date}
                onChange={(e) => setForm({ ...form, original_class_date: e.target.value })}
                data-testid="original-class-date"
              />
              <p className="text-[11px] text-slate-500 mt-1">{t("original_class_date_hint")}</p>
            </div>
          )}

          <div>
            <Label className="text-xs">{t("applies_to")}</Label>
            <Select
              value={form.enrollment_id}
              onValueChange={(v) => setForm({ ...form, enrollment_id: v })}
              disabled={!!existing?.id}
            >
              <SelectTrigger data-testid="session-applies-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t("entire_group")}</SelectItem>
                {enrollments.map((e) => {
                  const s = usersMap[e.student_id];
                  return (
                    <SelectItem key={e.id} value={e.id}>
                      {s?.name || e.student_id}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t("start_time")}</Label>
              <TimeSelect
                value={form.start_time}
                onChange={(v) => setForm({ ...form, start_time: v })}
                testId="session-start-time"
              />
              <p className="text-[11px] text-slate-500 mt-1">{t("session_time_hint")}</p>
            </div>
            <div>
              <Label className="text-xs">{t("duration_minutes")}</Label>
              <Input
                type="number"
                min={15}
                step={15}
                value={form.duration_minutes}
                onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                data-testid="session-duration"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            <Checkbox
              id="counts-toward-cycle"
              checked={!!form.counts_toward_cycle}
              onCheckedChange={(v) => setForm({ ...form, counts_toward_cycle: !!v })}
              data-testid="session-counts"
            />
            <Label htmlFor="counts-toward-cycle" className="text-sm cursor-pointer">
              {t("counts_toward_cycle")}
            </Label>
          </div>

          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            <Checkbox
              id="generates-charge"
              checked={!!form.generates_charge}
              onCheckedChange={(v) => setForm({ ...form, generates_charge: !!v })}
              data-testid="session-charge"
            />
            <Label htmlFor="generates-charge" className="text-sm cursor-pointer">
              {t("generates_charge")}
            </Label>
          </div>

          {form.generates_charge && (
            <div className="grid grid-cols-2 gap-3 -mt-2">
              <div>
                <Label className="text-xs">{t("charge_amount")}</Label>
                <Input
                  type="number"
                  step={0.01}
                  min={0}
                  value={form.charge_amount}
                  onChange={(e) => setForm({ ...form, charge_amount: e.target.value })}
                  data-testid="session-charge-amount"
                />
              </div>
              <div>
                <Label className="text-xs">{t("currency")}</Label>
                <Select
                  value={form.charge_currency}
                  onValueChange={(v) => setForm({ ...form, charge_currency: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="CRC">CRC</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs">{t("notes")}</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              placeholder={t("notes_placeholder_extra")}
              data-testid="session-notes"
            />
          </div>
        </div>

        {conflict && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="session-conflict">
            <p className="text-xs text-amber-800">{conflict}</p>
            <div className="flex gap-2 mt-2">
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" disabled={saving}
                      onClick={() => submit(true)} data-testid="session-conflict-force-btn">
                {t("move_booked_class_conflict_cta")}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setConflict(null)}
                      data-testid="session-conflict-cancel-btn">
                {t("move_booked_class_conflict_other")}
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          {existing?.id && onRequestUndo && (
            <Button
              variant="outline"
              onClick={() => { onOpenChange(false); onRequestUndo(existing); }}
              data-testid="undo-extra-session-btn"
              className="mr-auto text-red-700 border-red-300 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-1" /> {t("undo_session_tooltip")}
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("cancel")}
          </Button>
          <Button
            onClick={() => submit(false)}
            disabled={saving}
            className="btn-brand"
            data-testid="save-extra-session-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
            {t("save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
